import { Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';

export interface OrgImageSettings {
  compressionDefault: number;
  maxImageSizeBytes: number | null;
  maxImagesPerProduct: number | null;
}

export interface UpdateOrgImageSettingsInput {
  compressionDefault?: number;
  maxImageSizeBytes?: number | null;
  maxImagesPerProduct?: number | null;
}

@Injectable()
export class ImageSettingsService {
  constructor(private readonly prisma: PrismaService) {}

  async get(organizationId: string): Promise<OrgImageSettings> {
    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: organizationId },
      select: {
        imageCompressionDefault: true,
        maxImageSizeBytes: true,
        maxImagesPerProduct: true,
      },
    });

    return {
      compressionDefault: org.imageCompressionDefault,
      maxImageSizeBytes: org.maxImageSizeBytes,
      maxImagesPerProduct: org.maxImagesPerProduct,
    };
  }

  async update(organizationId: string, input: UpdateOrgImageSettingsInput): Promise<OrgImageSettings> {
    const org = await this.prisma.organization.update({
      where: { id: organizationId },
      data: {
        ...(input.compressionDefault !== undefined
          ? { imageCompressionDefault: this.clampCompression(input.compressionDefault) }
          : {}),
        ...(input.maxImageSizeBytes !== undefined ? { maxImageSizeBytes: input.maxImageSizeBytes } : {}),
        ...(input.maxImagesPerProduct !== undefined
          ? { maxImagesPerProduct: input.maxImagesPerProduct }
          : {}),
      },
      select: {
        imageCompressionDefault: true,
        maxImageSizeBytes: true,
        maxImagesPerProduct: true,
      },
    });

    return {
      compressionDefault: org.imageCompressionDefault,
      maxImageSizeBytes: org.maxImageSizeBytes,
      maxImagesPerProduct: org.maxImagesPerProduct,
    };
  }

  // 0 means "off" (no compression by default) — otherwise clamp into the
  // 5–95 range the upload UI's slider offers.
  private clampCompression(value: number): number {
    if (value <= 0) return 0;
    return Math.max(5, Math.min(95, Math.round(value)));
  }
}
